import { atom } from "jotai";
import { atomFamily, atomWithStorage, createJSONStorage } from "jotai/utils";
import type { DeliveryMethod } from "@/api/vendure/merchant/merchant.types";
import { currentVenueSlugAtom } from "@/stores/venue.store";

function venueSessionAtom<T>(keyPrefix: string, initial: T) {
  const family = atomFamily((slug: string) =>
    atomWithStorage<T>(
      `${keyPrefix}-${slug}`,
      initial,
      createJSONStorage<T>(() => window.sessionStorage),
    ),
  );
  return atom(
    (get): T => {
      const slug = get(currentVenueSlugAtom);
      return slug ? get(family(slug)) : initial;
    },
    (get, set, value: T) => {
      const slug = get(currentVenueSlugAtom);
      if (slug) set(family(slug), value);
    },
  );
}

export const DEFAULT_DELIVERY_METHOD: DeliveryMethod = "takeaway";
export const deliveryMethodAtom = venueSessionAtom<DeliveryMethod>(
  "munchi:delivery-method",
  DEFAULT_DELIVERY_METHOD,
);
export const orderTypeSheetOpenAtom = atom(false);
export const tableSpotSheetOpenAtom = atom(false);
export const loginSheetOpenAtom = atom(false);

export const selectedDayAtom = venueSessionAtom<number | null>(
  "munchi:selected-day-v2",
  null,
);
export const selectedTimeAtom = atom<string | null>(null);
export const venueIsOnlineAtom = atom<boolean>(true);

export interface DeliveryAddress {
  lat: number;
  lon: number;
  address: string;
  city: string;
  streetLine1?: string;
  streetLine2?: string;
  postalCode?: string;
  countryCode?: string;
  fullName?: string;
  locationType?: number;
  entrance?: string;
  floor?: string;
  instructions?: string;
}

export const deliveryAddressAtom = venueSessionAtom<DeliveryAddress | null>(
  "munchi:delivery-address",
  null,
);
export const tableSpotAtom = venueSessionAtom<string | null>(
  "munchi:table-spot",
  null,
);
export const tableSpotTypeAtom = venueSessionAtom<string>(
  "munchi:table-spot-type",
  "table",
);
export const deliveryInstructionsAtom = venueSessionAtom<string>(
  "munchi:delivery-instructions",
  "",
);
export const currencyCodeAtom = venueSessionAtom<string>(
  "munchi:currency-code",
  "EUR",
);
export const operationCountryAtom = venueSessionAtom<string | null>(
  "munchi:operation-country",
  null,
);

export const resetDeliverySessionAtom = atom(null, (_get, set) => {
  set(deliveryAddressAtom, null);
  set(deliveryInstructionsAtom, "");
  set(selectedDayAtom, null);
  set(selectedTimeAtom, null);
});
